import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { message } from "antd";
import { LoadingStatusCode } from "../../../../lib/types/LoadingStatusCode";
import { catchApiError } from "../../../../lib/utils/catchApiError";
import type { DictionaryDto } from "../../../../lib/interfaces";
import { ITypesCharacteristicsListSearch } from "../../types/TypesCharacteristicsListSearch";
import { ITypesCharacteristicsObjectListSearch } from "../../types/TypesCharacteristicsObjectListSearch";
import {
  addValuesTypesCharacteristicsObjectRoute,
  createTypesCharacteristicsObjectRoute,
  deleteTypesCharacteristicsObjectRoute,
  deleteValuesTypesCharacteristicsObjectRoute,
  getTypesCharacteristicsDicRoute,
  getTypesCharacteristicsDictionaryTypesRoute,
  getTypesCharacteristicsObjectObjectTypesDicRoute,
  searchTypesCharacteristicsByIdRoute,
  searchTypesCharacteristicsObjectByIdRoute,
  searchTypesCharacteristicsObjectRoute,
  searchTypesCharacteristicsRoute,
  updateTypesCharacteristicsObjectRoute,
} from "./typesCharacteristicsObjectRoutes";
import { TypeCharacteristic } from "../../types/TypeCharacteristic";
import { TypeCharacteristicObject } from "../../types/TypeCharacteristicObject";
import { camelizeString } from "../../utils/camelizeString";
import { IDeleteValuesTypesCharacteristicsParams } from "../../types/DeleteValuesTypesCharacteristicsParams";
import { IAddValuesTypesCharacteristicsObjectParams } from "../../types/AddValuesTypesCharacteristicsParams";
import { ITypesCharacteristicsObjectCreateParams } from "../../types/TypesCharacteristicsObjectCreateParams";
import { IDeleteTypesCharacteristicsParams } from "../../types/DeleteTypesCharacteristicsParams";
import { IUpdateTypesCharacteristicsParams } from "../../types/UpdateTypesCharacteristicsParams";

interface InitialState {
  typesCharacteristics: TypeCharacteristic[];
  typesCharacteristicsLoading: LoadingStatusCode;
  typesCharacteristicsObject: TypeCharacteristicObject[];
  typesCharacteristicsObjectLoading: LoadingStatusCode;
  typesCharacteristicsTypesDataDic: DictionaryDto[];
  typesCharacteristicsTypesDataDicLoading: LoadingStatusCode;
  typesCharacteristicsPagination: {
    current: number;
    total: number;
  };
  typesCharacteristicsTotalElements: number;
  typesCharacteristicsTypesDic: DictionaryDto[];
  typesCharacteristicsTypesDicLoading: LoadingStatusCode;
  typesCharacteristicsTypesValuesDic: DictionaryDto[];
  typesCharacteristicsTypesValuesDicLoading: LoadingStatusCode;
  typesCharacteristicsObjectObjectTypesDic: DictionaryDto[];
  typesCharacteristicsObjectObjectTypesDicLoading: LoadingStatusCode;
  typesCharacteristicsTypesUnitsDic: DictionaryDto[];
  typesCharacteristicsTypesUnitsDicLoading: LoadingStatusCode;
  typesCharacteristicsRangeTypesDic: DictionaryDto[];
  typesCharacteristicsRangeTypesDicLoading: LoadingStatusCode;
  typesCharacteristicsById?: TypeCharacteristic;
  typesCharacteristicsByIdLoading: LoadingStatusCode;
  typesCharacteristicsObjectById?: TypeCharacteristicObject;
  typesCharacteristicsObjectByIdLoading: LoadingStatusCode;
  createTypesCharacteristicsObjectLoading: LoadingStatusCode;
  deleteValuesTypesCharacteristicsObjectLoading: LoadingStatusCode;
  deleteTypesCharacteristicsObjectLoading: LoadingStatusCode;
  addValuesTypesCharacteristicsObjectLoading: LoadingStatusCode;
  typesCharacteristicsObjectAccessModesDic: DictionaryDto[];
  typesCharacteristicsObjectAccessModesDicLoading: LoadingStatusCode;
  typesCharacteristicsTypesStorageDic: DictionaryDto[];
  typesCharacteristicsTypesStorageDicLoading: LoadingStatusCode;
  typesCharacteristicsViewModeMaskDic: DictionaryDto[];
  typesCharacteristicsViewModeMaskDicLoading: LoadingStatusCode;
}

const initialState: InitialState = {
  typesCharacteristics: [],
  typesCharacteristicsLoading: "idle",
  typesCharacteristicsObject: [],
  typesCharacteristicsObjectLoading: "idle",
  typesCharacteristicsTypesDataDic: [],
  typesCharacteristicsTypesDataDicLoading: "idle",
  typesCharacteristicsPagination: {
    current: 0,
    total: 0,
  },
  typesCharacteristicsTotalElements: 0,
  typesCharacteristicsTypesDic: [],
  typesCharacteristicsTypesDicLoading: "idle",
  typesCharacteristicsTypesValuesDic: [],
  typesCharacteristicsTypesValuesDicLoading: "idle",
  typesCharacteristicsObjectObjectTypesDic: [],
  typesCharacteristicsObjectObjectTypesDicLoading: "idle",
  typesCharacteristicsTypesUnitsDic: [],
  typesCharacteristicsTypesUnitsDicLoading: "idle",
  typesCharacteristicsRangeTypesDic: [],
  typesCharacteristicsRangeTypesDicLoading: "idle",
  typesCharacteristicsById: undefined,
  typesCharacteristicsByIdLoading: "idle",
  typesCharacteristicsObjectById: undefined,
  typesCharacteristicsObjectByIdLoading: "idle",
  createTypesCharacteristicsObjectLoading: "idle",
  deleteValuesTypesCharacteristicsObjectLoading: "idle",
  deleteTypesCharacteristicsObjectLoading: "idle",
  addValuesTypesCharacteristicsObjectLoading: "idle",
  typesCharacteristicsObjectAccessModesDic: [],
  typesCharacteristicsObjectAccessModesDicLoading: "idle",
  typesCharacteristicsTypesStorageDic: [],
  typesCharacteristicsTypesStorageDicLoading: "idle",
  typesCharacteristicsViewModeMaskDic: [],
  typesCharacteristicsViewModeMaskDicLoading: "idle",
};

// Получение справочника типов данных
export const fetchTypesCharacteristicsObjectObjectTypesDic = createAsyncThunk(
  "typesCharacteristics/fetchTypesCharacteristicsObjectObjectTypesDic",
  async () => {
    try {
      const response = axios.get(getTypesCharacteristicsObjectObjectTypesDicRoute());
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки списка типов объектов. Попробуйте позже",
      });
    }
  }
);

// Получение справочника доступов
export const fetchTypesCharacteristicsObjectAccessModesDic = createAsyncThunk(
  "typesCharacteristics/fetchTypesCharacteristicsObjectAccessModesDic",
  async () => {
    try {
      const response = axios.get(getTypesCharacteristicsDicRoute("access_mode"));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки списка доступов. Попробуйте позже",
      });
    }
  }
);

// Получение справочника типов данных
export const fetchTypesCharacteristicsTypesDataDic = createAsyncThunk(
  "typesCharacteristics/fetchTypesCharacteristicsTypesDataDic",
  async () => {
    try {
      const response = axios.get(getTypesCharacteristicsDicRoute("data_type"));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки списка типов данных. Попробуйте позже",
      });
    }
  }
);

// Получение справочника типов значений
export const fetchTypesCharacteristicsTypesValuesDic = createAsyncThunk(
  "typesCharacteristics/fetchTypesCharacteristicsTypesValueDic",
  async () => {
    try {
      const response = axios.get(getTypesCharacteristicsDicRoute("value_type"));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки списка типов значений. Попробуйте позже",
      });
    }
  }
);

// Получение справочника типов хранения
export const fetchTypesCharacteristicsTypesStorageDic = createAsyncThunk(
  "typesCharacteristics/fetchTypesCharacteristicsTypesStorageDic",
  async () => {
    try {
      const response = axios.get(getTypesCharacteristicsDicRoute("storage_type"));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки списка типов хранения. Попробуйте позже",
      });
    }
  }
);

// Получение справочника типов отображения
export const fetchTypesCharacteristicsViewModeMaskDic = createAsyncThunk(
  "typesCharacteristics/fetchTypesCharacteristicsViewModeMaskDic",
  async () => {
    try {
      const response = axios.get(getTypesCharacteristicsDicRoute("view_mode_mask"));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки списка типов отображения. Попробуйте позже",
      });
    }
  }
);

// Получение справочника единиц измерения
export const fetchTypesCharacteristicsTypesUnitsDic = createAsyncThunk(
  "typesCharacteristics/fetchTypesCharacteristicsTypesUnitsDic",
  async () => {
    try {
      const response = axios.get(getTypesCharacteristicsDicRoute("price_unit"));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки списка единиц измерения. Попробуйте позже",
      });
    }
  }
);

// Получение справочника границ диапазона
export const fetchTypesCharacteristicsRangeTypesDic = createAsyncThunk(
  "typesCharacteristics/fetchTypesCharacteristicsRangeTypesDic",
  async () => {
    try {
      const response = axios.get(getTypesCharacteristicsDicRoute("range_type"));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки списка границ диапазона. Попробуйте позже",
      });
    }
  }
);

// Получение всех типов словарей
export const fetchTypesCharacteristicsTypesDic = createAsyncThunk(
  "typesCharacteristics/fetchTypesCharacteristicsTypesDic",
  async () => {
    try {
      const response = axios.get(getTypesCharacteristicsDictionaryTypesRoute());
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки списка типов словарей. Попробуйте позже",
      });
    }
  }
);

// Получение информации о переданном справочнике
export const fetchTypesCharacteristicsSelectValueDic = createAsyncThunk(
  "typesCharacteristics/fetchTypesCharacteristicsSelectValueDic",
  async (type: string) => {
    try {
      const response = axios.get(getTypesCharacteristicsDicRoute(type));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки информации о выбранном справочнике. Попробуйте позже",
      });
    }
  }
);

// Поиск типов характеристик
export const searchTypesCharacteristics = createAsyncThunk(
  "typesCharacteristics/searchTypesCharacteristics",
  async (searchParams: ITypesCharacteristicsListSearch) => {
    try {
      const response = axios.get(searchTypesCharacteristicsRoute(searchParams));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки загрузки типов характеристик. Попробуйте позже",
      });
    }
  }
);

// Поиск типов характеристик (объект)
export const searchTypesCharacteristicsObject = createAsyncThunk(
  "typesCharacteristics/searchTypesCharacteristicsObject",
  async (searchParams: ITypesCharacteristicsObjectListSearch) => {
    try {
      const response = axios.get(searchTypesCharacteristicsObjectRoute(searchParams));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки загрузки типов характеристик (объект). Попробуйте позже",
      });
    }
  }
);

// Получение информации о Типе Характеристики
export const searchTypesCharacteristicsById = createAsyncThunk(
  "typesCharacteristics/searchTypesCharacteristicsById",
  async (typesCharacteristicsId: string) => {
    try {
      const response = axios.get(searchTypesCharacteristicsByIdRoute(typesCharacteristicsId));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки информации о типе характеристики. Попробуйте позже",
      });
    }
  }
);

// Получение информации о Типе Характеристики (объект)
export const searchTypesCharacteristicsObjectById = createAsyncThunk(
  "typesCharacteristics/searchTypesCharacteristicsObjectById",
  async ({
    objectTypeId,
    objectCharTypeId,
    onDate,
  }: {
    objectTypeId: string;
    objectCharTypeId: string;
    onDate?: string;
  }) => {
    try {
      const response = axios.get(
        searchTypesCharacteristicsObjectByIdRoute(objectTypeId, objectCharTypeId, onDate)
      );
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки информации о типе характеристики (объект). Попробуйте позже",
      });
    }
  }
);

// Создание Типа Характеристики (объект)
export const createTypesCharacteristicsObject = createAsyncThunk(
  "typesCharacteristics/createTypesCharacteristicsObject",
  async (body: { objectTypeId: number; params: ITypesCharacteristicsObjectCreateParams }) => {
    try {
      const response = axios.post(
        createTypesCharacteristicsObjectRoute(body.objectTypeId),
        body.params
      );
      const { data } = await response;

      message.success("Тип характеристики (объект) успешно создан");

      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка создания типа характеристики. Попробуйте позже",
      });
    }
  }
);

// Изменение даты закрытия Значения Типа Характеристики (объект)
export const deleteValuesTypesCharacteristicsObject = createAsyncThunk(
  "typesCharacteristics/deleteValuesTypesCharacteristicsObject",
  async (deleteParams: IDeleteValuesTypesCharacteristicsParams) => {
    try {
      const response = axios.delete(deleteValuesTypesCharacteristicsObjectRoute(deleteParams));
      const { data } = await response;

      message.destroy();
      message.success("Дата закрытия успешно обновлена");

      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка изменения даты закрытия. Попробуйте позже",
      });
    }
  }
);

// Добавление Значения Типа Характеристики (объект)
export const addValuesTypesCharacteristicsObject = createAsyncThunk(
  "typesCharacteristics/addValuesTypesCharacteristicsObject",
  async (params: IAddValuesTypesCharacteristicsObjectParams) => {
    try {
      const response = axios.post(
        addValuesTypesCharacteristicsObjectRoute(params.objectCharTypeId),
        params.body
      );
      const { data } = await response;

      message.destroy();
      message.success("Значение типа характеристики (объект) успешно добавлено");

      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка добавления значения типа характеристики (объект). Попробуйте позже",
      });
    }
  }
);

// Изменение даты закрытия Типа Характеристики (объект)
export const deleteTypesCharacteristicsObject = createAsyncThunk(
  "typesCharacteristics/deleteTypesCharacteristicsObject",
  async (deleteParams: IDeleteTypesCharacteristicsParams) => {
    try {
      const response = axios.delete(deleteTypesCharacteristicsObjectRoute(deleteParams));
      const { data } = await response;

      message.destroy();
      message.success("Дата закрытия успешно обновлена");

      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка изменения даты закрытия. Попробуйте позже",
      });
    }
  }
);

// Изменение Типа Характеристики (объект)
export const updateTypesCharacteristicsObject = createAsyncThunk(
  "typesCharacteristics/updateTypesCharacteristicsObject",
  async (updateParams: IUpdateTypesCharacteristicsParams) => {
    try {
      const response = axios.patch(
        updateTypesCharacteristicsObjectRoute(updateParams.query),
        updateParams.body
      );
      const { data } = await response;

      message.destroy();
      message.success("Тип характеристики (объект) успешно изменен");

      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка изменения типа характеристики (объект). Попробуйте позже",
      });
    }
  }
);

export const typesCharacteristicsObjectSlice = createSlice({
  name: "typesCharacteristics",
  initialState,
  reducers: {
    cleanTypesCharacteristics: (state) => {
      state.typesCharacteristicsLoading = "idle";
      state.typesCharacteristics = [];
    },
    cleanTypesCharacteristicsObjectById: (state) => {
      state.typesCharacteristicsObjectByIdLoading = "rejected";
      state.typesCharacteristicsObjectById = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchTypesCharacteristics.pending, (state) => {
        state.typesCharacteristicsLoading = "pending";
      })
      .addCase(searchTypesCharacteristics.rejected, (state) => {
        state.typesCharacteristicsLoading = "rejected";
        state.typesCharacteristics = [];
        state.typesCharacteristicsTotalElements = 0;
        state.typesCharacteristicsPagination = {
          current: 0,
          total: 0,
        };
      })
      .addCase(searchTypesCharacteristics.fulfilled, (state, { payload }) => {
        state.typesCharacteristicsLoading = "fulfilled";
        state.typesCharacteristics = payload?.content;
        state.typesCharacteristicsTotalElements = payload.totalElements;
        state.typesCharacteristicsPagination = {
          current: payload.number,
          total: payload.totalElements,
        };
      })
      .addCase(searchTypesCharacteristicsObject.pending, (state) => {
        state.typesCharacteristicsObjectLoading = "pending";
      })
      .addCase(searchTypesCharacteristicsObject.rejected, (state) => {
        state.typesCharacteristicsObjectLoading = "rejected";
        state.typesCharacteristicsObject = [];
      })
      .addCase(searchTypesCharacteristicsObject.fulfilled, (state, { payload }) => {
        state.typesCharacteristicsObjectLoading = "fulfilled";
        state.typesCharacteristicsObject = payload;
      })
      .addCase(fetchTypesCharacteristicsTypesDataDic.pending, (state) => {
        state.typesCharacteristicsTypesDataDicLoading = "pending";
      })
      .addCase(fetchTypesCharacteristicsTypesDataDic.rejected, (state) => {
        state.typesCharacteristicsTypesDataDicLoading = "rejected";
        state.typesCharacteristicsTypesDataDic = [];
      })
      .addCase(fetchTypesCharacteristicsTypesDataDic.fulfilled, (state, { payload }) => {
        state.typesCharacteristicsTypesDataDicLoading = "fulfilled";
        state.typesCharacteristicsTypesDataDic = payload;
      })
      .addCase(fetchTypesCharacteristicsTypesValuesDic.pending, (state) => {
        state.typesCharacteristicsTypesValuesDicLoading = "pending";
      })
      .addCase(fetchTypesCharacteristicsTypesValuesDic.rejected, (state) => {
        state.typesCharacteristicsTypesValuesDicLoading = "rejected";
        state.typesCharacteristicsTypesValuesDic = [];
      })
      .addCase(fetchTypesCharacteristicsTypesValuesDic.fulfilled, (state, { payload }) => {
        state.typesCharacteristicsTypesValuesDicLoading = "fulfilled";
        state.typesCharacteristicsTypesValuesDic = payload;
      })
      .addCase(fetchTypesCharacteristicsTypesUnitsDic.pending, (state) => {
        state.typesCharacteristicsTypesUnitsDicLoading = "pending";
      })
      .addCase(fetchTypesCharacteristicsTypesUnitsDic.rejected, (state) => {
        state.typesCharacteristicsTypesUnitsDicLoading = "rejected";
        state.typesCharacteristicsTypesUnitsDic = [];
      })
      .addCase(fetchTypesCharacteristicsTypesUnitsDic.fulfilled, (state, { payload }) => {
        state.typesCharacteristicsTypesUnitsDicLoading = "fulfilled";
        state.typesCharacteristicsTypesUnitsDic = payload;
      })
      .addCase(fetchTypesCharacteristicsRangeTypesDic.pending, (state) => {
        state.typesCharacteristicsRangeTypesDicLoading = "pending";
      })
      .addCase(fetchTypesCharacteristicsRangeTypesDic.rejected, (state) => {
        state.typesCharacteristicsRangeTypesDicLoading = "rejected";
        state.typesCharacteristicsRangeTypesDic = [];
      })
      .addCase(fetchTypesCharacteristicsRangeTypesDic.fulfilled, (state, { payload }) => {
        state.typesCharacteristicsRangeTypesDicLoading = "fulfilled";
        state.typesCharacteristicsRangeTypesDic = payload;
      })
      .addCase(searchTypesCharacteristicsById.pending, (state) => {
        state.typesCharacteristicsByIdLoading = "pending";
      })
      .addCase(searchTypesCharacteristicsById.rejected, (state) => {
        state.typesCharacteristicsByIdLoading = "rejected";
        state.typesCharacteristicsById = undefined;
      })
      .addCase(searchTypesCharacteristicsById.fulfilled, (state, { payload }) => {
        state.typesCharacteristicsByIdLoading = "fulfilled";
        state.typesCharacteristicsById = payload;
      })
      .addCase(searchTypesCharacteristicsObjectById.pending, (state) => {
        state.typesCharacteristicsObjectByIdLoading = "pending";
      })
      .addCase(searchTypesCharacteristicsObjectById.rejected, (state) => {
        state.typesCharacteristicsObjectByIdLoading = "rejected";
        state.typesCharacteristicsObjectById = undefined;
      })
      .addCase(searchTypesCharacteristicsObjectById.fulfilled, (state, { payload }) => {
        state.typesCharacteristicsObjectByIdLoading = "fulfilled";
        state.typesCharacteristicsObjectById = payload?.[0];
      })
      .addCase(fetchTypesCharacteristicsTypesDic.pending, (state) => {
        state.typesCharacteristicsTypesDicLoading = "pending";
      })
      .addCase(fetchTypesCharacteristicsTypesDic.rejected, (state) => {
        state.typesCharacteristicsTypesDicLoading = "rejected";
        state.typesCharacteristicsTypesDic = [];
      })
      .addCase(fetchTypesCharacteristicsTypesDic.fulfilled, (state, { payload }) => {
        state.typesCharacteristicsTypesDicLoading = "fulfilled";
        state.typesCharacteristicsTypesDic = payload;
      })
      .addCase(
        fetchTypesCharacteristicsSelectValueDic.pending,
        (state, { meta: { arg: dicTypeAlias } }) => {
          const camelizeDicTypeAlias = camelizeString(dicTypeAlias);
          state[`${camelizeDicTypeAlias}Loading`] = "pending";
        }
      )
      .addCase(
        fetchTypesCharacteristicsSelectValueDic.rejected,
        (state, { meta: { arg: dicTypeAlias } }) => {
          const camelizeDicTypeAlias = camelizeString(dicTypeAlias);
          state[`${camelizeDicTypeAlias}Loading`] = "rejected";
        }
      )
      .addCase(fetchTypesCharacteristicsSelectValueDic.fulfilled, (state, action) => {
        const dicTypeAlias = action.meta.arg;
        const camelizeDicTypeAlias = camelizeString(dicTypeAlias);

        state[`${camelizeDicTypeAlias}Loading`] = "fulfilled";
        state[camelizeDicTypeAlias] = action.payload;
      })
      .addCase(createTypesCharacteristicsObject.pending, (state) => {
        state.createTypesCharacteristicsObjectLoading = "pending";
      })
      .addCase(createTypesCharacteristicsObject.rejected, (state) => {
        state.createTypesCharacteristicsObjectLoading = "rejected";
      })
      .addCase(createTypesCharacteristicsObject.fulfilled, (state, { payload }) => {
        state.createTypesCharacteristicsObjectLoading = "fulfilled";
      })
      .addCase(deleteValuesTypesCharacteristicsObject.pending, (state) => {
        state.deleteValuesTypesCharacteristicsObjectLoading = "pending";
      })
      .addCase(deleteValuesTypesCharacteristicsObject.rejected, (state) => {
        state.deleteValuesTypesCharacteristicsObjectLoading = "rejected";
      })
      .addCase(deleteValuesTypesCharacteristicsObject.fulfilled, (state, { payload }) => {
        state.deleteValuesTypesCharacteristicsObjectLoading = "fulfilled";
      })
      .addCase(deleteTypesCharacteristicsObject.pending, (state) => {
        state.deleteTypesCharacteristicsObjectLoading = "pending";
      })
      .addCase(deleteTypesCharacteristicsObject.rejected, (state) => {
        state.deleteTypesCharacteristicsObjectLoading = "rejected";
      })
      .addCase(deleteTypesCharacteristicsObject.fulfilled, (state, { payload }) => {
        state.deleteTypesCharacteristicsObjectLoading = "fulfilled";
      })
      .addCase(addValuesTypesCharacteristicsObject.pending, (state) => {
        state.addValuesTypesCharacteristicsObjectLoading = "pending";
      })
      .addCase(addValuesTypesCharacteristicsObject.rejected, (state) => {
        state.addValuesTypesCharacteristicsObjectLoading = "rejected";
      })
      .addCase(addValuesTypesCharacteristicsObject.fulfilled, (state, { payload }) => {
        state.addValuesTypesCharacteristicsObjectLoading = "fulfilled";
      })
      .addCase(fetchTypesCharacteristicsObjectObjectTypesDic.pending, (state) => {
        state.typesCharacteristicsObjectObjectTypesDicLoading = "pending";
      })
      .addCase(fetchTypesCharacteristicsObjectObjectTypesDic.rejected, (state) => {
        state.typesCharacteristicsObjectObjectTypesDicLoading = "rejected";
        state.typesCharacteristicsObjectObjectTypesDic = [];
      })
      .addCase(fetchTypesCharacteristicsObjectObjectTypesDic.fulfilled, (state, { payload }) => {
        state.typesCharacteristicsObjectObjectTypesDicLoading = "fulfilled";
        state.typesCharacteristicsObjectObjectTypesDic = payload;
      })
      .addCase(fetchTypesCharacteristicsObjectAccessModesDic.pending, (state) => {
        state.typesCharacteristicsObjectAccessModesDicLoading = "pending";
      })
      .addCase(fetchTypesCharacteristicsObjectAccessModesDic.rejected, (state) => {
        state.typesCharacteristicsObjectAccessModesDicLoading = "rejected";
        state.typesCharacteristicsObjectAccessModesDic = [];
      })
      .addCase(fetchTypesCharacteristicsObjectAccessModesDic.fulfilled, (state, { payload }) => {
        state.typesCharacteristicsObjectAccessModesDicLoading = "fulfilled";
        state.typesCharacteristicsObjectAccessModesDic = payload;
      })
      .addCase(fetchTypesCharacteristicsTypesStorageDic.pending, (state) => {
        state.typesCharacteristicsTypesStorageDicLoading = "pending";
      })
      .addCase(fetchTypesCharacteristicsTypesStorageDic.rejected, (state) => {
        state.typesCharacteristicsTypesStorageDicLoading = "rejected";
        state.typesCharacteristicsTypesStorageDic = [];
      })
      .addCase(fetchTypesCharacteristicsTypesStorageDic.fulfilled, (state, { payload }) => {
        state.typesCharacteristicsTypesStorageDicLoading = "fulfilled";
        state.typesCharacteristicsTypesStorageDic = payload;
      })
      .addCase(fetchTypesCharacteristicsViewModeMaskDic.pending, (state) => {
        state.typesCharacteristicsViewModeMaskDicLoading = "pending";
      })
      .addCase(fetchTypesCharacteristicsViewModeMaskDic.rejected, (state) => {
        state.typesCharacteristicsViewModeMaskDicLoading = "rejected";
        state.typesCharacteristicsViewModeMaskDic = [];
      })
      .addCase(fetchTypesCharacteristicsViewModeMaskDic.fulfilled, (state, { payload }) => {
        state.typesCharacteristicsViewModeMaskDicLoading = "fulfilled";
        state.typesCharacteristicsViewModeMaskDic = payload;
      });
  },
});

export const {
  reducer: typesCharacteristicsObjectReducer,
  actions: typesCharacteristicsObjectActions,
} = typesCharacteristicsObjectSlice;
